import { Classes, Colors, Dialog, NonIdealState } from "@blueprintjs/core";
import { FormikProps } from "formik";
import {
  ButtonsBloc,
  FGCheckboxInput,
  FGCustomPanel,
  FGListen,
  FGTextAreaInput,
  FieldGroup,
  InlineButtonContainer,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";
import * as Yup from "yup";

import {
  DogApi,
  EDogType,
  ERequestState,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
  TransientWorkbookGridDto,
  WorkbookRequestApi,
  WorkbookRequestDto,
} from "../../../../../api";
import {
  CancelButton,
  FGClubSelect,
  FGDogSearchInput,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  InformationTooltip,
  SharepointButton,
  SmallFormGenerator,
  UploadSharepointButton,
} from "../../../../../components";
import { useDialog, useEventsContext, useGlobalData, useNavigationContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { TransientWorkbookList } from "./TransientWorkbookList";
import { WorkbookSelectionPanel } from "./WorkbookSelectionPanel";

export interface IWorkbookRequestDetailsProps {
  workbookRequestId: number;
  editMode: boolean;
  requestInfo?: RequestInfoDto;
}

export const WorkbookRequestDetails: React.FunctionComponent<IWorkbookRequestDetailsProps> = ({
  workbookRequestId,
  requestInfo,
  editMode,
}) => {
  const { masterRequestId = "0" } = useParams<{
    masterRequestId: string;
  }>();
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(WorkbookRequestApi);
  const dogApi = useApiService(DogApi);
  const personApi = useApiService(PersonApi);
  const [showWorkbookPrintDialog, setShowWorkbookPrintDialog] = React.useState(false);
  const [reprintWorkbookLoading, setreprintWorkbookLoading] = React.useState(false);
  const { openNewTab } = useWindow();
  const { goBack } = useNavigationContext();
  const { showDialogPromise } = useDialog();
  const { currentLanguage } = useGlobalData();

  const reprintWorkbook = React.useCallback(
    async (workbookId: number, workboookRequestId: number) => {
      setShowWorkbookPrintDialog(false);
      try {
        await api.reprintWorkbook(workbookId, workboookRequestId);
        showSuccess(t(ETLCodes.PrintSuccess));
      } catch {
        showError(t(ETLCodes.Error));
      }
      setreprintWorkbookLoading(false);
    },
    [api, t]
  );

  const { data, loading, saving, saveItem, refresh } = useCrudApi<WorkbookRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = workbookRequestId) => {
          if (id > 0) {
            return api.get(id, currentLanguage.languageId);
          } else if (+masterRequestId > 0) {
            return api.getSubRequest(+masterRequestId, currentLanguage.languageId);
          } else {
            return WorkbookRequestDto.fromJS({
              request: RequestDto.fromJS({
                requestDate: new Date(),
                remark: "",
                executionDate: null,
                status: ERequestState.REQUESTED,
              }),
            }).toJSON();
          }
        },
        saveApiFn: (d: WorkbookRequestDto) => api.save(d),
        onSavedRoute: (d) => `${ERoutes.workbookRequest}/${d.workbookRequestId}/detail/edit`,
      }),
      [api, currentLanguage.languageId, masterRequestId, workbookRequestId]
    ),
    [workbookRequestId]
  );

  const editableStatus = React.useMemo(
    () =>
      data?.request?.status === ERequestState.REQUESTED &&
      (requestInfo?.masterRequestStateCode === ERequestState.REQUESTED || requestInfo?.masterRequestStateCode == null),
    [data, requestInfo]
  );

  const blocked = React.useMemo(
    () =>
      requestInfo?.isRequestBlocked ||
      requestInfo?.isMasterRequestBlocked ||
      requestInfo?.requestStateCode === ERequestState.EXECUTED ||
      (requestInfo?.masterRequestStateCode != null &&
        !(requestInfo?.masterRequestStateCode === ERequestState.REQUESTED)),
    [requestInfo]
  );
  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      let isBanned = false;
      if (workbookRequestId <= 0 && values?.request?.personId) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [workbookRequestId, personApi, saveItem, showDialogPromise, t]
  );

  const [clubId, setclubId] = React.useState(0);
  const [dogIdValue, setDogIdValue] = React.useState(data?.dogId ?? 0);

  React.useEffect(() => {
    if (data) {
      setDogIdValue(data.dogId);
    }
  }, [data]);

  //REFRESH THE REQUEST ON STATE CHANGE
  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      dogId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test("dog-deceased", t(ETLCodes.DogIsDeceased), async function (value) {
          if (+masterRequestId === 0) {
            if (workbookRequestId <= 0 || (data?.request?.status === ERequestState.REQUESTED && !!value)) {
              const res = await dogApi.isDogDeceased(value);
              return !res.value;
            }
          }

          return true;
        })
        .test("is-not-excluded", t(ETLCodes.DogIsExcluded), async function (value) {
          if (+masterRequestId === 0) {
            if (workbookRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await dogApi.isDogExcluded(value);
              return !result.value;
            }
          }
          return true;
        })
        .test("dog-type", t(ETLCodes.DogIsNotBelgianOrImportOrForeignOrWithoutPedigree), async function (value) {
          if (+masterRequestId === 0) {
            const type = await dogApi.getDogType(value);
            return (
              type === EDogType.BelgianDogWithPedigree ||
              type === EDogType.ImportDog ||
              type === EDogType.ForeignDog ||
              type === EDogType.DogWithoutPedigree
            );
          }
          return true;
        }),
      clubId: Yup.number().nullable(),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-deseased", t(ETLCodes.PersonISDeceased), async function (value) {
            if (+masterRequestId === 0 && !!value) {
              if (value && (workbookRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED)) {
                const result = await personApi.isDeceased(value);
                return !result.value;
              }
            }
            return true;
          })
          .test("is-belgian", t(ETLCodes.IsNotBelgian), async function (value) {
            if (+masterRequestId === 0 && !!value) {
              if (workbookRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                const result = await personApi.isPersonBelgian(value);
                return result.value;
              }
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (+masterRequestId === 0) {
              if (workbookRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
                if (!!value) {
                  const result = await personApi.missingAddressInformationCheck(value);
                  return result.value;
                }
                return true;
              }
            }
            return true;
          })
          .test("temporary-owner", t(ETLCodes.OwnerIsTemporaryError), async function (value) {
            if (+masterRequestId === 0 && !!value) {
              const res = await dogApi.isDogTemporaryOwner(dogIdValue, value);
              return !res.value;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [data?.request?.status, dogApi, dogIdValue, masterRequestId, personApi, t, workbookRequestId]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const UpdateListFormik = React.useCallback(
    (newList: TransientWorkbookGridDto[], formik: FormikProps<WorkbookRequestDto>) => {
      formik?.setFieldValue("transientWorkbooksGridDtos", newList);
      formik?.setFieldTouched("transientWorkbooksGridDtos", true);
    },
    []
  );

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  const [dogShpLoading, setClubShpLoading] = React.useState(false);
  const dogExistsInShp = React.useMemo(() => !!data?.dogSharepointUri, [data?.dogSharepointUri]);
  const openOrCreateDogInShp = React.useCallback(async () => {
    setClubShpLoading(true);
    if (!!data?.dogSharepointUri) {
      window.open(data?.dogSharepointUri, "_blank");
      setClubShpLoading(false);
    } else {
      try {
        await dogApi.createInSharepoint(data?.dogId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setClubShpLoading(false);
      }
    }
  }, [data?.dogSharepointUri, data?.dogId, dogApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.workbookRequest)}
        saving={saving || saveChecking}
        hideButtons={!editableStatus}
      >
        <FGListen
          field="dogId"
          onChanged={(value, formik) => {
            setDogIdValue(value);
            if (!clubId || clubId < 1) {
              if (+masterRequestId === 0 && formik.dirty) {
                formik.setFieldValue("request.personId", null);
              }
            }
          }}
        />
        <FGListen
          field="clubId"
          onChanged={(value, formik) => {
            setclubId(value);
            if (+masterRequestId === 0 && formik.dirty) {
              formik.setFieldValue("request.personId", null);
            }
          }}
        />
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.RequestData),
            rightElement: workbookRequestId > 0 && (
              <>
                <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
                {data?.request?.status === ERequestState.EXECUTED ? (
                  <UploadSharepointButton
                    text={t(ETLCodes.ReprintWorkbook)}
                    loading={reprintWorkbookLoading}
                    onClick={() => {
                      setShowWorkbookPrintDialog(true);
                      setreprintWorkbookLoading(true);
                    }}
                  />
                ) : null}
              </>
            ),
          }}
        >
          <div>
            <FGSRSHDateMaskInput
              name="request.requestDate"
              label={t(ETLCodes.RequestDate)}
              requiredMark
              readonly={!editableStatus || blocked || requestInfo?.masterRequestId > 0}
            />
            {data?.request?.status === ERequestState.EXECUTED && (
              <FieldGroup columns={2}>
                <FGSRSHDateMaskInput name="request.executionDate" label={t(ETLCodes.ClosingDate)} readonly={true} />
              </FieldGroup>
            )}
            <FGClubSelect
              name="clubId"
              label={t(ETLCodes.Club)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.club}/${item}/detail/view`)}
              readonly={!editableStatus || blocked}
              visible={!(requestInfo?.masterRequestId > 0)}
            />
            <FGDogSearchInput
              name="dogId"
              label={t(ETLCodes.Dog)}
              onItemViewClick={(item) => openNewTab(`${ERoutes.dog}/${item}/detail/view`)}
              readonly={!editableStatus || blocked || requestInfo?.masterRequestId > 0}
              info={
                workbookRequestId > 0 && (
                  <SharepointButton
                    existsInShp={dogExistsInShp}
                    loading={dogShpLoading}
                    onClick={openOrCreateDogInShp}
                  />
                )
              }
            />
            <FGPeopleSearchInput
              name="request.personId"
              label={t(ETLCodes.Contact)}
              clubId={clubId ? clubId : null}
              dogId={dogIdValue && !clubId ? dogIdValue : null}
              onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
              requiredMark
              readonly={!editableStatus || blocked || requestInfo?.masterRequestId > 0}
              info={<InformationTooltip tooltipMessage={ETLCodes.CanBeOwnerOrOtherPersonInformation} />}
              disabled={(!clubId && !dogIdValue) || (clubId < 0 && dogIdValue < 0)}
              showWarning={true}
            />
          </div>
          <FGTextAreaInput name="request.remark" label={t(ETLCodes.Remark)} rows={6} />
          <FGCheckboxInput label={t(ETLCodes.RegisteredLetter)} name="registeredLetter" />
        </FieldGroup>
        <FGCustomPanel>
          {({ formik }) =>
            formik?.values?.dogId ? (
              <TransientWorkbookList
                dogId={formik?.values?.dogId}
                initialWorkbooks={formik?.values?.transientWorkbooksGridDtos || []}
                onWorkbookChanged={(newWorkbooksList) => UpdateListFormik(newWorkbooksList, formik)}
                editMode={editableStatus && editMode && !blocked}
                loading={loading}
              />
            ) : (
              <NonIdealState icon="resolve" description={t(ETLCodes.PleaseSelectDogToAddWorkbooks)} />
            )
          }
        </FGCustomPanel>

        {data?.request?.status === ERequestState.EXECUTED && !(requestInfo?.masterRequestId > 0) && (
          <InlineButtonContainer>
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => goBack(`${ERoutes.workbookRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
      <Dialog
        isOpen={showWorkbookPrintDialog}
        title={t(ETLCodes.SelectContact)}
        className={""}
        canOutsideClickClose={true}
        style={{ width: 1200, background: Colors.WHITE }}
        onClose={() => {
          setShowWorkbookPrintDialog(false);
          setreprintWorkbookLoading(false);
        }}
      >
        <div className={Classes.DIALOG_BODY}>
          <WorkbookSelectionPanel
            workbookRequestId={workbookRequestId}
            onWorkbookSelected={(workbookId) => reprintWorkbook(workbookId, workbookRequestId)}
          />
        </div>
      </Dialog>
    </>
  );
};
